/**
 * SmartLink anti-fraud pre-filter — dashboard page.
 *
 * Operator surface for the /api/v1/smartlink-fraud backend (feature #112).
 * Three regions on one page:
 *
 *   1. Rule editor — pick kind, fill dynamic fields, scope to a SmartLink
 *      or leave blank for tenant-wide. POST creates the rule.
 *   2. Rules table — lists all rules for the currently scoped SmartLink
 *      (or tenant-wide when the scope input is empty). Soft-delete on
 *      click; deleted rows stay visible with a struck-through style.
 *   3. Test-check panel — post a synthetic click {ip, ua, country, click_id}
 *      to /check and surface pass/block + reason.
 *
 * Brand: emerald→sky gradient headline, Inter, zinc borders, 6-8px rounded,
 * light theme only. No emojis, no competitor names.
 *
 * The file is intentionally monolithic — breaking fields into external files
 * would bust the owned-file contract. Internal sub-components live here.
 */

import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
	applySoftDelete,
	checkSample,
	COMMON_COUNTRIES,
	countActive,
	createRule,
	deleteRule,
	EMPTY_CHECK,
	EMPTY_DRAFT,
	type CheckInput,
	type CheckResult,
	type FraudRuleRow,
	listRules,
	RULE_KIND_DESCRIPTIONS,
	RULE_KIND_LABELS,
	RULE_KINDS,
	type RuleDraft,
	type RuleKind,
	ruleDraftSchema,
	scopeLabel,
	smartlinkFraudQueryKeys,
	summariseRule,
} from "./api";

// ---------------------------------------------------------------------------
// Static style tokens.
// ---------------------------------------------------------------------------

const CARD =
	"rounded-[8px] border border-zinc-200 bg-white shadow-[0_1px_3px_0_rgb(24_24_27/0.08),0_4px_8px_-2px_rgb(24_24_27/0.06)]";
const LABEL = "text-sm font-medium text-zinc-700";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 disabled:bg-zinc-50 disabled:text-zinc-500";
const HINT = "text-xs text-zinc-500";
const ERROR_TEXT = "text-xs text-red-600";
const BTN_PRIMARY =
	"rounded-[6px] border border-emerald-600 bg-emerald-600 px-4 py-2 text-sm font-medium text-white shadow-[0_1px_2px_0_rgb(24_24_27/0.06)] hover:bg-emerald-700 hover:border-emerald-700 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_SECONDARY =
	"rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60";

// ---------------------------------------------------------------------------
// Top-level page
// ---------------------------------------------------------------------------

export interface SmartlinkFraudPageProps {
	initialSmartlinkId?: string;
}

export function SmartlinkFraudPage({ initialSmartlinkId }: SmartlinkFraudPageProps) {
	const queryClient = useQueryClient();

	const [scope, setScope] = useState<string>(initialSmartlinkId ?? "");
	const [draft, setDraft] = useState<RuleDraft>({
		...EMPTY_DRAFT,
		smartlinkId: initialSmartlinkId ?? "",
	});
	const [draftErrors, setDraftErrors] = useState<Record<string, string>>({});
	const [check, setCheck] = useState<CheckInput>({
		...EMPTY_CHECK,
		smartlink_id: initialSmartlinkId ?? "",
	});
	const [checkResult, setCheckResult] = useState<CheckResult | null>(null);

	const trimmedScope = scope.trim();
	const canQueryRules = trimmedScope.length > 0;

	// --- Rules list query ---------------------------------------------------
	const rulesQuery = useQuery({
		queryKey: smartlinkFraudQueryKeys.byScope(trimmedScope),
		queryFn: () => listRules(trimmedScope),
		enabled: canQueryRules,
	});

	// --- Mutations ----------------------------------------------------------
	const createMutation = useMutation({
		mutationFn: createRule,
		onSuccess: (row) => {
			toast.success("Rule saved.");
			// Reset draft but preserve smartlinkId scope + selected kind.
			setDraft((prev) => ({
				...EMPTY_DRAFT,
				smartlinkId: prev.smartlinkId,
				kind: prev.kind,
			}));
			setDraftErrors({});
			// Refresh the rules list for the active scope.
			const scopeKey = row.smartlink_id ?? trimmedScope;
			queryClient.invalidateQueries({
				queryKey: smartlinkFraudQueryKeys.byScope(scopeKey),
			});
			if (row.smartlink_id === null && trimmedScope.length > 0) {
				queryClient.invalidateQueries({
					queryKey: smartlinkFraudQueryKeys.byScope(trimmedScope),
				});
			}
		},
		onError: (err: Error) => {
			toast.error(err.message || "Unable to save rule.");
		},
	});

	const deleteMutation = useMutation({
		mutationFn: (id: string) => deleteRule(id),
		onSuccess: (_void, id) => {
			toast.success("Rule removed.");
			queryClient.setQueryData<FraudRuleRow[]>(
				smartlinkFraudQueryKeys.byScope(trimmedScope),
				(prev) =>
					prev ? applySoftDelete(prev, id, Math.floor(Date.now() / 1000)) : prev,
			);
		},
		onError: (err: Error) => {
			toast.error(err.message || "Unable to remove rule.");
		},
	});

	const checkMutation = useMutation({
		mutationFn: checkSample,
		onSuccess: (result) => {
			setCheckResult(result);
			if (result.allowed) {
				toast.success("Sample passed the pre-filter.");
			} else {
				toast.error(`Sample blocked: ${result.reason}`);
			}
		},
		onError: (err: Error) => {
			toast.error(err.message || "Unable to run check.");
		},
	});

	// --- Derived ------------------------------------------------------------
	const rows = rulesQuery.data ?? [];
	const activeCount = useMemo(() => countActive(rows), [rows]);
	const deletedCount = rows.length - activeCount;

	// --- Handlers -----------------------------------------------------------
	function handleScopeChange(value: string) {
		setScope(value);
	}

	function handleKindChange(kind: RuleKind) {
		setDraft((prev) => ({ ...prev, kind }));
		setDraftErrors({});
	}

	function handleDraftSubmit(event: React.FormEvent<HTMLFormElement>) {
		event.preventDefault();
		const parsed = ruleDraftSchema.safeParse(draft);
		if (!parsed.success) {
			setDraftErrors(parsed.errors);
			toast.error("Please fix the highlighted fields.");
			return;
		}
		setDraftErrors({});
		createMutation.mutate(parsed.data);
	}

	function handleDelete(id: string) {
		deleteMutation.mutate(id);
	}

	function handleCheckSubmit(event: React.FormEvent<HTMLFormElement>) {
		event.preventDefault();
		if (!check.smartlink_id.trim() || !check.click_id.trim()) {
			toast.error("smartlink_id and click_id are required.");
			return;
		}
		checkMutation.mutate(check);
	}

	// --- Render -------------------------------------------------------------
	return (
		<main className="mx-auto flex w-full max-w-5xl flex-col gap-8 px-6 py-10 sm:py-16">
			<Header />

			<ScopeCard
				scope={scope}
				onChange={handleScopeChange}
				loading={rulesQuery.isFetching}
				activeCount={activeCount}
				deletedCount={deletedCount}
			/>

			<RuleEditor
				draft={draft}
				onDraftChange={setDraft}
				onKindChange={handleKindChange}
				errors={draftErrors}
				onSubmit={handleDraftSubmit}
				busy={createMutation.isPending}
			/>

			<RulesTable
				rows={rows}
				busy={deleteMutation.isPending}
				deletingId={deleteMutation.variables ?? null}
				canQuery={canQueryRules}
				loading={rulesQuery.isLoading}
				onDelete={handleDelete}
			/>

			<CheckPanel
				check={check}
				onChange={setCheck}
				onSubmit={handleCheckSubmit}
				result={checkResult}
				busy={checkMutation.isPending}
			/>

			<DocHint />
		</main>
	);
}

// ---------------------------------------------------------------------------
// Header / scope / fragments
// ---------------------------------------------------------------------------

function Header() {
	return (
		<header className="flex flex-col gap-2">
			<p className="text-xs font-semibold uppercase tracking-[0.12em] text-zinc-500">
				SmartLinks
			</p>
			<h1 className="text-brand-gradient text-3xl font-semibold tracking-tight sm:text-4xl">
				Anti-fraud pre-filter
			</h1>
			<p className="max-w-prose text-sm text-zinc-600">
				Block clicks before they hit attribution. Configure IP blocklists,
				user-agent patterns, country deny-lists, and per-IP click-rate caps.
				Rules can apply to one SmartLink or the whole tenant.
			</p>
		</header>
	);
}

function ScopeCard(props: {
	scope: string;
	onChange: (v: string) => void;
	loading: boolean;
	activeCount: number;
	deletedCount: number;
}) {
	return (
		<section className={`${CARD} flex flex-col gap-4 p-6`}>
			<div className="flex flex-col gap-1.5">
				<label htmlFor="scope-smartlink-id" className={LABEL}>
					SmartLink ID (scope)
				</label>
				<input
					id="scope-smartlink-id"
					type="text"
					autoComplete="off"
					placeholder="e.g. smart_14ab9f"
					className={INPUT}
					value={props.scope}
					onChange={(e) => props.onChange(e.currentTarget.value)}
				/>
				<p className={HINT}>
					Shows rules scoped to this SmartLink plus all tenant-wide rules.
					Leave blank to target tenant-wide rules only.
				</p>
			</div>
			<div className="flex flex-wrap items-center gap-2 text-xs text-zinc-600">
				<Stat label="Active" value={props.activeCount} tone="emerald" />
				<Stat label="Deleted" value={props.deletedCount} tone="zinc" />
				{props.loading && (
					<span className="ml-auto text-zinc-500" aria-live="polite">
						Loading.
					</span>
				)}
			</div>
		</section>
	);
}

function Stat(props: { label: string; value: number; tone: "emerald" | "zinc" }) {
	const tones = {
		emerald: "border-emerald-200 bg-emerald-50 text-emerald-700",
		zinc: "border-zinc-200 bg-zinc-50 text-zinc-700",
	} as const;
	return (
		<span
			className={`inline-flex items-center gap-2 rounded-[6px] border px-2 py-1 ${tones[props.tone]}`}
		>
			<span className="font-medium">{props.label}</span>
			<span className="font-semibold tabular-nums">{props.value}</span>
		</span>
	);
}

// ---------------------------------------------------------------------------
// Rule editor card
// ---------------------------------------------------------------------------

function RuleEditor(props: {
	draft: RuleDraft;
	onDraftChange: (d: RuleDraft) => void;
	onKindChange: (k: RuleKind) => void;
	errors: Record<string, string>;
	onSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
	busy: boolean;
}) {
	const { draft, onDraftChange, onKindChange, errors, busy } = props;

	function patch<K extends keyof RuleDraft>(key: K, value: RuleDraft[K]) {
		onDraftChange({ ...draft, [key]: value });
	}

	return (
		<section className={`${CARD} flex flex-col gap-6 p-6`}>
			<header className="flex flex-col gap-1">
				<h2 className="text-lg font-semibold text-zinc-900">New rule</h2>
				<p className={HINT}>{RULE_KIND_DESCRIPTIONS[draft.kind]}</p>
			</header>

			<form
				onSubmit={props.onSubmit}
				className="flex flex-col gap-6"
				noValidate
				data-testid="rule-editor-form"
			>
				<KindToggle value={draft.kind} onChange={onKindChange} />

				<div className="flex flex-col gap-1.5">
					<label htmlFor="draft-smartlink-id" className={LABEL}>
						SmartLink ID <span className="font-normal text-zinc-400">(optional)</span>
					</label>
					<input
						id="draft-smartlink-id"
						name="smartlinkId"
						type="text"
						autoComplete="off"
						placeholder="Leave blank for tenant-wide"
						className={INPUT}
						value={draft.smartlinkId}
						onChange={(e) => patch("smartlinkId", e.currentTarget.value)}
						aria-invalid={Boolean(errors.smartlinkId)}
					/>
					{errors.smartlinkId && <p className={ERROR_TEXT}>{errors.smartlinkId}</p>}
				</div>

				<KindFields
					draft={draft}
					onChange={onDraftChange}
					errors={errors}
				/>

				<div className="flex flex-col gap-4 border-t border-zinc-200 pt-4 sm:flex-row sm:items-center sm:justify-end">
					<button type="submit" disabled={busy} className={BTN_PRIMARY}>
						{busy ? "Saving." : "Save rule"}
					</button>
				</div>
			</form>
		</section>
	);
}

function KindToggle(props: { value: RuleKind; onChange: (k: RuleKind) => void }) {
	return (
		<fieldset className="flex flex-col gap-2">
			<legend className={LABEL}>Rule kind</legend>
			<div
				role="radiogroup"
				aria-label="Rule kind"
				className="inline-flex flex-wrap gap-1 rounded-[6px] border border-zinc-200 bg-zinc-50 p-0.5"
			>
				{RULE_KINDS.map((kind) => (
					<KindButton
						key={kind}
						active={props.value === kind}
						label={RULE_KIND_LABELS[kind]}
						kind={kind}
						onClick={() => props.onChange(kind)}
					/>
				))}
			</div>
		</fieldset>
	);
}

function KindButton(props: {
	active: boolean;
	label: string;
	kind: RuleKind;
	onClick: () => void;
}) {
	const base =
		"rounded-[6px] px-3 py-1.5 text-sm font-medium transition-colors";
	const active = "bg-white text-zinc-900 shadow-[0_1px_2px_0_rgb(24_24_27/0.06)]";
	const inactive = "text-zinc-600 hover:text-zinc-900";
	return (
		<button
			type="button"
			role="radio"
			aria-checked={props.active}
			data-testid={`kind-${props.kind}`}
			onClick={props.onClick}
			className={`${base} ${props.active ? active : inactive}`}
		>
			{props.label}
		</button>
	);
}

// ---------------------------------------------------------------------------
// Dynamic kind-specific fields. Each kind renders its own inputs under a
// common wrapper. The test suite pins on `data-testid` markers here.
// ---------------------------------------------------------------------------

function KindFields(props: {
	draft: RuleDraft;
	onChange: (d: RuleDraft) => void;
	errors: Record<string, string>;
}) {
	const { draft, onChange, errors } = props;

	function patch<K extends keyof RuleDraft>(key: K, value: RuleDraft[K]) {
		onChange({ ...draft, [key]: value });
	}

	switch (draft.kind) {
		case "ip_blocklist":
			return (
				<div
					className="flex flex-col gap-1.5"
					data-testid="fields-ip-blocklist"
				>
					<label htmlFor="field-ip-list" className={LABEL}>
						IP or CIDR list
					</label>
					<textarea
						id="field-ip-list"
						name="ipList"
						rows={4}
						placeholder={"1.2.3.4\n10.0.0.0/16\n2001:db8::/32"}
						className={`${INPUT} font-mono`}
						value={draft.ipList}
						onChange={(e) => patch("ipList", e.currentTarget.value)}
						aria-invalid={Boolean(errors.ipList)}
					/>
					<p className={HINT}>
						One entry per line. Bare IPs or CIDR notation. IPv4 and IPv6.
					</p>
					{errors.ipList && <p className={ERROR_TEXT}>{errors.ipList}</p>}
				</div>
			);
		case "ua_regex":
			return (
				<div
					className="flex flex-col gap-1.5"
					data-testid="fields-ua-regex"
				>
					<label htmlFor="field-ua-regex" className={LABEL}>
						User-agent regex
					</label>
					<input
						id="field-ua-regex"
						name="uaRegex"
						type="text"
						autoComplete="off"
						placeholder="headless|bot|crawler"
						className={`${INPUT} font-mono`}
						value={draft.uaRegex}
						onChange={(e) => patch("uaRegex", e.currentTarget.value)}
						aria-invalid={Boolean(errors.uaRegex)}
					/>
					<p className={HINT}>JavaScript RegExp source. Flags not supported.</p>
					{errors.uaRegex && <p className={ERROR_TEXT}>{errors.uaRegex}</p>}
				</div>
			);
		case "country_deny":
			return (
				<div
					className="flex flex-col gap-1.5"
					data-testid="fields-country-deny"
				>
					<span className={LABEL}>Countries to deny</span>
					<CountryPicker
						selected={draft.countries}
						onChange={(codes) => patch("countries", codes)}
					/>
					<p className={HINT}>
						Select ISO 3166-1 alpha-2 codes. Click a chip to remove.
					</p>
					{errors.countries && (
						<p className={ERROR_TEXT}>{errors.countries}</p>
					)}
				</div>
			);
		case "click_rate_cap":
			return (
				<div
					className="flex flex-col gap-4 sm:flex-row"
					data-testid="fields-click-rate-cap"
				>
					<div className="flex flex-1 flex-col gap-1.5">
						<label htmlFor="field-threshold" className={LABEL}>
							Threshold (clicks)
						</label>
						<input
							id="field-threshold"
							name="threshold"
							type="number"
							min={1}
							step={1}
							inputMode="numeric"
							className={`${INPUT} max-w-xs`}
							value={draft.threshold}
							onChange={(e) => patch("threshold", e.currentTarget.value)}
							aria-invalid={Boolean(errors.threshold)}
						/>
						<p className={HINT}>Per IP over a rolling 60-second window.</p>
						{errors.threshold && (
							<p className={ERROR_TEXT}>{errors.threshold}</p>
						)}
					</div>
					<div className="flex flex-1 flex-col gap-1.5">
						<label htmlFor="field-rate-label" className={LABEL}>
							Scope label
						</label>
						<input
							id="field-rate-label"
							name="rateLabel"
							type="text"
							autoComplete="off"
							placeholder="e.g. mobile_peak"
							className={INPUT}
							value={draft.rateLabel}
							onChange={(e) => patch("rateLabel", e.currentTarget.value)}
						/>
						<p className={HINT}>
							Free-form tag shown in the rules table. Use "*" for any IP.
						</p>
					</div>
				</div>
			);
	}
}

function CountryPicker(props: {
	selected: string[];
	onChange: (codes: string[]) => void;
}) {
	const [query, setQuery] = useState("");
	const filtered = useMemo(() => {
		const q = query.trim().toLowerCase();
		if (q.length === 0) return COMMON_COUNTRIES;
		return COMMON_COUNTRIES.filter(
			(c) =>
				c.code.toLowerCase().includes(q) ||
				c.name.toLowerCase().includes(q),
		);
	}, [query]);

	function toggle(code: string) {
		const set = new Set(props.selected);
		if (set.has(code)) set.delete(code);
		else set.add(code);
		props.onChange(Array.from(set));
	}

	return (
		<div className="flex flex-col gap-2">
			{props.selected.length > 0 && (
				<div className="flex flex-wrap gap-1.5">
					{props.selected.map((code) => (
						<button
							key={code}
							type="button"
							onClick={() => toggle(code)}
							className="inline-flex items-center gap-2 rounded-[6px] border border-emerald-500 bg-emerald-50 px-2 py-1 text-xs font-medium text-emerald-700 hover:bg-emerald-100"
							aria-label={`Remove ${code}`}
						>
							{code}
							<span aria-hidden="true" className="text-emerald-500">
								&times;
							</span>
						</button>
					))}
				</div>
			)}
			<input
				type="text"
				autoComplete="off"
				placeholder="Search countries."
				className={INPUT}
				value={query}
				onChange={(e) => setQuery(e.currentTarget.value)}
			/>
			<div className="flex max-h-48 flex-wrap gap-1.5 overflow-y-auto rounded-[6px] border border-zinc-200 bg-zinc-50 p-2">
				{filtered.map((c) => {
					const active = props.selected.includes(c.code);
					return (
						<button
							key={c.code}
							type="button"
							onClick={() => toggle(c.code)}
							aria-pressed={active}
							className={`inline-flex items-center gap-1 rounded-[6px] border px-2 py-1 text-xs font-medium transition-colors ${
								active
									? "border-emerald-500 bg-emerald-50 text-emerald-700"
									: "border-zinc-200 bg-white text-zinc-700 hover:border-zinc-300"
							}`}
						>
							<span className="tabular-nums">{c.code}</span>
							<span className="text-zinc-500">{c.name}</span>
						</button>
					);
				})}
				{filtered.length === 0 && (
					<p className={HINT}>No matches for that search.</p>
				)}
			</div>
		</div>
	);
}

// ---------------------------------------------------------------------------
// Rules table
// ---------------------------------------------------------------------------

function RulesTable(props: {
	rows: FraudRuleRow[];
	busy: boolean;
	deletingId: string | null;
	canQuery: boolean;
	loading: boolean;
	onDelete: (id: string) => void;
}) {
	const { rows, canQuery, loading } = props;

	return (
		<section className={`${CARD} flex flex-col gap-4 p-6`}>
			<header className="flex flex-col gap-1">
				<h2 className="text-lg font-semibold text-zinc-900">Rules</h2>
				<p className={HINT}>
					Rules apply in order: IP blocklist, UA regex, country deny, click-rate
					cap. The first match blocks.
				</p>
			</header>

			{!canQuery && (
				<p className="text-sm text-zinc-500">
					Enter a SmartLink ID above to load rules scoped to that link (plus
					tenant-wide rules).
				</p>
			)}

			{canQuery && loading && (
				<p className="text-sm text-zinc-500">Loading rules.</p>
			)}

			{canQuery && !loading && rows.length === 0 && (
				<p className="text-sm text-zinc-500">
					No rules yet. Add one above to enable the pre-filter.
				</p>
			)}

			{rows.length > 0 && (
				<div className="overflow-x-auto rounded-[6px] border border-zinc-200">
					<table className="min-w-full divide-y divide-zinc-200 text-sm">
						<thead className="bg-zinc-50 text-left text-xs font-semibold uppercase tracking-wide text-zinc-600">
							<tr>
								<th className="px-3 py-2">Kind</th>
								<th className="px-3 py-2">Scope</th>
								<th className="px-3 py-2">Params</th>
								<th className="px-3 py-2">Status</th>
								<th className="px-3 py-2 text-right">Actions</th>
							</tr>
						</thead>
						<tbody
							className="divide-y divide-zinc-100"
							data-testid="rules-tbody"
						>
							{rows.map((row) => (
								<RuleRow
									key={row.id}
									row={row}
									busy={props.busy && props.deletingId === row.id}
									onDelete={() => props.onDelete(row.id)}
								/>
							))}
						</tbody>
					</table>
				</div>
			)}
		</section>
	);
}

function RuleRow(props: {
	row: FraudRuleRow;
	busy: boolean;
	onDelete: () => void;
}) {
	const { row } = props;
	const rowClasses = row.active
		? "text-zinc-900"
		: "text-zinc-400 line-through";
	return (
		<tr className={rowClasses} data-testid={`rule-row-${row.id}`}>
			<td className="px-3 py-2 font-medium">{RULE_KIND_LABELS[row.rule_type]}</td>
			<td className="px-3 py-2 text-xs text-zinc-600">{scopeLabel(row)}</td>
			<td className="px-3 py-2 font-mono text-xs">{summariseRule(row)}</td>
			<td className="px-3 py-2 text-xs">
				{row.active ? (
					<span className="inline-flex items-center gap-1 rounded-[6px] border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-emerald-700">
						Active
					</span>
				) : (
					<span className="inline-flex items-center gap-1 rounded-[6px] border border-zinc-200 bg-zinc-50 px-2 py-0.5 text-zinc-500">
						Deleted
					</span>
				)}
			</td>
			<td className="px-3 py-2 text-right">
				{row.active ? (
					<button
						type="button"
						onClick={props.onDelete}
						disabled={props.busy}
						className={BTN_SECONDARY}
						data-testid={`delete-${row.id}`}
					>
						{props.busy ? "Removing." : "Soft-delete"}
					</button>
				) : (
					<span className={HINT}>Archived</span>
				)}
			</td>
		</tr>
	);
}

// ---------------------------------------------------------------------------
// Test-check panel
// ---------------------------------------------------------------------------

function CheckPanel(props: {
	check: CheckInput;
	onChange: (next: CheckInput) => void;
	onSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
	result: CheckResult | null;
	busy: boolean;
}) {
	const { check, onChange, result, busy } = props;

	function patch<K extends keyof CheckInput>(key: K, value: CheckInput[K]) {
		onChange({ ...check, [key]: value });
	}

	return (
		<section className={`${CARD} flex flex-col gap-4 p-6`}>
			<header className="flex flex-col gap-1">
				<h2 className="text-lg font-semibold text-zinc-900">Try a sample click</h2>
				<p className={HINT}>
					Run a synthetic click through the pre-filter to verify rules behave as
					expected. Blocked samples are logged as audit hits.
				</p>
			</header>

			<form
				onSubmit={props.onSubmit}
				className="grid grid-cols-1 gap-4 sm:grid-cols-2"
				noValidate
			>
				<Field label="SmartLink ID" htmlFor="check-smartlink-id">
					<input
						id="check-smartlink-id"
						type="text"
						autoComplete="off"
						className={INPUT}
						value={check.smartlink_id}
						onChange={(e) => patch("smartlink_id", e.currentTarget.value)}
					/>
				</Field>
				<Field label="Click ID" htmlFor="check-click-id">
					<input
						id="check-click-id"
						type="text"
						autoComplete="off"
						className={INPUT}
						value={check.click_id}
						onChange={(e) => patch("click_id", e.currentTarget.value)}
					/>
				</Field>
				<Field label="IP" htmlFor="check-ip">
					<input
						id="check-ip"
						type="text"
						autoComplete="off"
						placeholder="203.0.113.9"
						className={`${INPUT} font-mono`}
						value={check.ip}
						onChange={(e) => patch("ip", e.currentTarget.value)}
					/>
				</Field>
				<Field label="Country (ISO)" htmlFor="check-country">
					<input
						id="check-country"
						type="text"
						maxLength={2}
						autoComplete="off"
						placeholder="US"
						className={INPUT}
						value={check.country}
						onChange={(e) => patch("country", e.currentTarget.value.toUpperCase())}
					/>
				</Field>
				<Field label="User-Agent" htmlFor="check-ua" full>
					<input
						id="check-ua"
						type="text"
						autoComplete="off"
						placeholder="Mozilla/5.0 ."
						className={`${INPUT} font-mono`}
						value={check.ua}
						onChange={(e) => patch("ua", e.currentTarget.value)}
					/>
				</Field>
				<div className="col-span-full flex items-center justify-end gap-2 border-t border-zinc-200 pt-4">
					<button type="submit" disabled={busy} className={BTN_PRIMARY}>
						{busy ? "Checking." : "Run check"}
					</button>
				</div>
			</form>

			{result && <CheckResultView result={result} />}
		</section>
	);
}

function Field(props: {
	label: string;
	htmlFor: string;
	full?: boolean;
	children: React.ReactNode;
}) {
	return (
		<div className={`flex flex-col gap-1.5 ${props.full ? "sm:col-span-2" : ""}`}>
			<label htmlFor={props.htmlFor} className={LABEL}>
				{props.label}
			</label>
			{props.children}
		</div>
	);
}

function CheckResultView(props: { result: CheckResult }) {
	const { result } = props;
	if (result.allowed) {
		return (
			<aside
				className="rounded-[6px] border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800"
				data-testid="check-result"
			>
				<p className="font-medium">Allowed</p>
				<p className="text-emerald-700">
					The sample passed every active rule.
				</p>
			</aside>
		);
	}
	return (
		<aside
			className="rounded-[6px] border border-red-200 bg-red-50 p-3 text-sm text-red-800"
			data-testid="check-result"
		>
			<p className="font-medium">Blocked</p>
			<p className="text-red-700">
				Reason: <span className="font-mono">{result.reason}</span>
			</p>
			<p className="text-xs text-red-600">
				Rule ID: <span className="font-mono">{result.rule_id}</span>
				{result.hit_id ? (
					<>
						{" "}
						· Hit ID: <span className="font-mono">{result.hit_id}</span>
					</>
				) : null}
			</p>
		</aside>
	);
}

// ---------------------------------------------------------------------------
// Footer hint
// ---------------------------------------------------------------------------

function DocHint() {
	return (
		<aside className="rounded-[8px] border border-zinc-200 bg-zinc-50 p-4 text-sm text-zinc-600">
			<p className="font-medium text-zinc-800">How it works</p>
			<ul className="mt-2 flex list-inside list-disc flex-col gap-1">
				<li>
					Rules are evaluated at the tracker edge before the SmartLink resolves.
				</li>
				<li>
					The first matching rule blocks the click and records an audit hit.
				</li>
				<li>
					Deleted rules are soft-deleted — they stay visible for audit but are
					no longer enforced.
				</li>
				<li>
					Tenant-wide rules apply to every SmartLink; per-link rules narrow the
					scope.
				</li>
			</ul>
		</aside>
	);
}

export default SmartlinkFraudPage;
